// Aquí están los thunks relacionados con embajadores
import { createAsyncThunk } from "@reduxjs/toolkit";
import api from "@/services/api";
// Slice para activar Embajador
import {
  activarEmbajadorSuccess,
  activarEmbajadorFailure,
} from "./activarEmbajador";
// Slice para links de referido
import {
  generateLinkStart,
  generateLinkSuccess,
  generateLinkFailure,
  validateCodeStart,
  validateCodeSuccess,
  validateCodeFailure,
  trackClickStart,
  trackClickSuccess,
  trackClickFailure,
  getMyLinksStart,
  getMyLinksSuccess,
  getMyLinksFailure,
} from "./referralLinkSlice";

/**
 * 📝 EMBAJADOR — Activar Embajador
 * Crea el embajador si no existe y envía la información para activación
 */
export const activarEmbajador = createAsyncThunk(
  "embajador/activarEmbajador",
  async (embajadorData, { dispatch, rejectWithValue }) => {
    try {
      console.log("Activar Embajador Data in Thunk:", embajadorData);
      
      // Crear FormData para enviar los archivos junto con los demás datos
      const formData = new FormData();
      
      // Agregar campos de texto obligatorios
      formData.append("idUsuario", embajadorData.idUsuario);
      formData.append("rut", embajadorData.rut);
      formData.append("instagram", embajadorData.instagram); // Obligatorio
      
      // Agregar campos de texto opcionales (redes sociales)
      if (embajadorData.facebook) {
        formData.append("facebook", embajadorData.facebook);
      }
      if (embajadorData.tiktok) {
        formData.append("tiktok", embajadorData.tiktok);
      }
      
      // Agregar archivos obligatorios
      if (embajadorData.Frente_cedula_identidad) {
        formData.append("Frente_cedula_identidad", embajadorData.Frente_cedula_identidad);
      }
      if (embajadorData.Dorso_cedula_identidad) {
        formData.append("Dorso_cedula_identidad", embajadorData.Dorso_cedula_identidad);
      }
      if (embajadorData.antecedentes) {
        formData.append("antecedentes", embajadorData.antecedentes);
      }
      if (embajadorData.cuenta_bancaria) {
        formData.append("cuenta_bancaria", embajadorData.cuenta_bancaria);
      }

      const { data } = await api.post("/embajadores/activar", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      
      dispatch(activarEmbajadorSuccess({ info: data }));
      return data;
    } catch (err) {
      console.error("Error al activar el embajador:", err);
      let errorMessage;
      if (!err.response) {
        errorMessage = "La solicitud no ha sido enviada por problemas de conexión al servidor";
      } else {
        errorMessage = err.response.data?.message ||
                       err.response.data?.error ||
                       "Error al activar el embajador";
      }
      dispatch(activarEmbajadorFailure(errorMessage));
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * 📝 EMBAJADOR REFERRAL — Generar Link de Referido
 * Genera o recupera el link de referido único para un embajador en un evento específico
 */
export const generateReferralLink = createAsyncThunk(
  "embajador/generateReferralLink",
  async ({ eventoUid }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(generateLinkStart());
      
      const { data } = await api.post("/embajadores/referral/generate", {
        eventoUid,
      });
      
      dispatch(generateLinkSuccess(data));
      return data;
    } catch (err) {
      console.error("Error al generar link de referido:", err);
      const errorMessage = err.response?.data?.message || 
                          "Error al generar el link de referido";
      dispatch(generateLinkFailure(errorMessage));
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * 📝 EMBAJADOR REFERRAL — Validar Código de Referido
 * Valida que un código de referido sea válido para un evento específico
 */
export const validateReferralCode = createAsyncThunk(
  "embajador/validateReferralCode",
  async ({ referralCode, eventoUid }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(validateCodeStart());
      
      const { data } = await api.get(
        `/embajadores/referral/validate/${referralCode}/${eventoUid}`
      );
      
      dispatch(validateCodeSuccess(data));
      return data;
    } catch (err) {
      console.error("Error al validar código de referido:", err);
      const errorMessage = err.response?.data?.message || 
                          "Error al validar el código de referido";
      dispatch(validateCodeFailure(errorMessage));
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * 📝 EMBAJADOR REFERRAL — Registrar Click de Referido
 * Registra un click en un link de referido para tracking
 */
export const trackReferralClick = createAsyncThunk(
  "embajador/trackReferralClick",
  async ({ referralCode, eventoUid }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(trackClickStart());
      
      const { data } = await api.post("/embajadores/referral/track-click", {
        referralCode,
        eventoUid,
      });
      
      dispatch(trackClickSuccess(data));
      return data;
    } catch (err) {
      console.error("Error al registrar click de referido:", err);
      const errorMessage = err.response?.data?.message || 
                          "Error al registrar el click";
      dispatch(trackClickFailure(errorMessage));
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * 📝 EMBAJADOR REFERRAL — Obtener Mis Links de Referido
 * Obtiene los links de referido del embajador con filtrado, paginación y ordenamiento
 * 
 * @param {Object} params - Parámetros de consulta
 * @param {string} params.eventoFechaHoraInicioDesde - Fecha mínima de inicio del evento
 * @param {string} params.eventoFechaHoraInicioHasta - Fecha máxima de inicio del evento
 * @param {string} params.eventoFechaHoraFinDesde - Fecha mínima de fin del evento
 * @param {string} params.eventoFechaHoraFinHasta - Fecha máxima de fin del evento
 * @param {string} params.eventoEstado - Estado del evento (activo|inactivo|cancelado)
 * @param {string} params.eventoNombre - Búsqueda parcial por nombre del evento
 * @param {string} params.sortBy - Campo para ordenar (eventoFechaHoraInicio|eventoFechaHoraFin|eventoNombre|createdAt|totalClicks|totalConversions|totalSales)
 * @param {string} params.sortOrder - Orden (asc|desc)
 * @param {number} params.page - Número de página
 * @param {number} params.limit - Resultados por página
 */
export const getMyReferralLinks = createAsyncThunk(
  "embajador/getMyReferralLinks",
  async (params = {}, { dispatch, rejectWithValue }) => {
    try {
      dispatch(getMyLinksStart());
      
      // Construir query string con los parámetros
      const queryParams = new URLSearchParams();
      
      if (params.eventoFechaHoraInicioDesde) {
        queryParams.append('eventoFechaHoraInicioDesde', params.eventoFechaHoraInicioDesde);
      }
      if (params.eventoFechaHoraInicioHasta) {
        queryParams.append('eventoFechaHoraInicioHasta', params.eventoFechaHoraInicioHasta);
      }
      if (params.eventoFechaHoraFinDesde) {
        queryParams.append('eventoFechaHoraFinDesde', params.eventoFechaHoraFinDesde);
      }
      if (params.eventoFechaHoraFinHasta) {
        queryParams.append('eventoFechaHoraFinHasta', params.eventoFechaHoraFinHasta);
      }
      if (params.eventoEstado) {
        queryParams.append('eventoEstado', params.eventoEstado);
      }
      if (params.eventoNombre) {
        queryParams.append('eventoNombre', params.eventoNombre);
      }
      if (params.sortBy) {
        queryParams.append('sortBy', params.sortBy);
      }
      if (params.sortOrder) {
        queryParams.append('sortOrder', params.sortOrder);
      }
      if (params.page) {
        queryParams.append('page', params.page);
      }
      if (params.limit) {
        queryParams.append('limit', params.limit);
      }
      
      const queryString = queryParams.toString();
      const url = `/embajadores/referral/my-links${queryString ? `?${queryString}` : ''}`;
      
      const { data } = await api.get(url);
      
      dispatch(getMyLinksSuccess(data.links || []));
      return data;
    } catch (err) {
      console.error("Error al obtener links de referido:", err);
      const errorMessage = err.response?.data?.message || 
                          "Error al obtener tus links de referido";
      dispatch(getMyLinksFailure(errorMessage));
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * 📝 EMBAJADOR — Obtener Todos los Embajadores
 * Obtiene embajadores con filtrado, paginación y selección de campos
 * 
 * @param {Object} params - Parámetros de consulta
 * @param {string} params.search - Búsqueda parcial por nombre, apellido o ciudad
 * @param {number} params.enabled - Filtro por estado habilitado (1) o deshabilitado (0)
 * @param {string} params.camposResponse - Campos separados por coma (ej: 'id,nombre,apellido,imgPerfil')
 * @param {string} params.sortBy - Campo para ordenar (nombre|apellido|ciudad|createdAt)
 * @param {string} params.sortOrder - Orden (asc|desc)
 * @param {number} params.page - Número de página
 * @param {number} params.limit - Resultados por página
 */
export const getAllEmbajadores = createAsyncThunk(
  "embajador/getAllEmbajadores",
  async (params = {}, { rejectWithValue }) => {
    try {
      // Construir query string con los parámetros
      const queryParams = new URLSearchParams();
      
      if (params.search) {
        queryParams.append('search', params.search);
      }
      if (params.enabled !== undefined && params.enabled !== null) {
        queryParams.append('enabled', params.enabled);
      }
      if (params.camposResponse) {
        queryParams.append('camposResponse', params.camposResponse);
      }
      if (params.sortBy) {
        queryParams.append('sortBy', params.sortBy);
      }
      if (params.sortOrder) {
        queryParams.append('sortOrder', params.sortOrder);
      }
      if (params.page) {
        queryParams.append('page', params.page);
      }
      if (params.limit) {
        queryParams.append('limit', params.limit);
      }
      
      const queryString = queryParams.toString();
      const url = `/embajadores/all${queryString ? `?${queryString}` : ''}`;
      
      const { data } = await api.get(url);
      
      return data;
    } catch (err) {
      console.error("Error al obtener embajadores:", err);
      const errorMessage = err.response?.data?.message || 
                          "Error al obtener embajadores";
      return rejectWithValue(errorMessage);
    }
  }
);
